import { useState } from 'react'
import { InlineStack, Button, Modal, Banner } from '@shopify/polaris'
import { isCapturable, isCancellable, isRecreatable, TERMINAL_SUCCESS } from '../lib/status'
import { currencySymbol } from '../lib/currency'

type Props = {
    status: string | null
    amount: string
    currency: string
    onRequest: () => void
    onCapture: () => void
    onRevoke: () => void
    onRefresh: () => void
    loading: boolean
    activeAction: string | null
    invoiceUrl?: string | null
    proxyUrl?: string | null
    paymentId?: string | null
    amountDrift?: boolean
    // True while an authorization call is in flight (manual or automatic).
    // Disables Request Guarantee so a merchant can't fire a duplicate request.
    requesting?: boolean
}

export function ActionButtons({
    status, amount, currency, onRequest, onCapture, onRevoke, onRefresh,
    loading, activeAction, invoiceUrl, proxyUrl, amountDrift, requesting,
}: Props) {
    const [modalOpen, setModalOpen] = useState(false)
    const [iframeError, setIframeError] = useState(false)

    const symbol = currencySymbol(currency)
    const noPayment = !status
    const capturable = isCapturable(status)
    const cancellable = isCancellable(status)
    const recreatable = isRecreatable(status)
    const captured = !!status && TERMINAL_SUCCESS.includes(status)

    // proxyUrl is a server-signed, shop-scoped URL (the proxy rejects unsigned
    // requests). Append download=1 respecting its existing query string.
    const downloadUrl = proxyUrl ? `${proxyUrl}${proxyUrl.includes('?') ? '&' : '?'}download=1` : null
    const showInvoiceActions = captured && invoiceUrl && proxyUrl

    return (
        <>
            <InlineStack gap="300">
                {(noPayment || recreatable || requesting) && (
                    <Button
                        variant="primary"
                        onClick={onRequest}
                        loading={activeAction === 'requesting' || requesting}
                        disabled={loading || !!activeAction || requesting}
                    >
                        Request Guarantee
                    </Button>
                )}
                {capturable && (
                    <Button
                        variant="primary"
                        tone="success"
                        onClick={onCapture}
                        loading={activeAction === 'capturing'}
                        // Block invoicing on amount drift: capturing the new total would
                        // exceed the authorized amount and Svea rejects it. Revoke +
                        // re-request first (the drift banner explains this).
                        disabled={loading || !!activeAction || amountDrift}
                    >
                        Invoice ({symbol}{parseFloat(amount).toFixed(2)})
                    </Button>
                )}
                {cancellable && (
                    <Button
                        variant="primary"
                        tone="critical"
                        onClick={onRevoke}
                        loading={activeAction === 'revoking'}
                        disabled={loading || !!activeAction}
                    >
                        Revoke
                    </Button>
                )}
                {showInvoiceActions && (
                    <>
                        <Button onClick={() => { setIframeError(false); setModalOpen(true) }}>
                            Preview Invoice
                        </Button>
                        <Button url={downloadUrl!} target="_blank">
                            Download Invoice
                        </Button>
                    </>
                )}
                <Button
                    onClick={onRefresh}
                    disabled={loading || !!activeAction}
                >
                    Refresh
                </Button>
            </InlineStack>

            {showInvoiceActions && (
                <Modal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    title="Invoice Preview"
                    secondaryActions={[
                        { content: 'Download Invoice', url: downloadUrl!, target: '_blank' },
                    ]}
                >
                    <Modal.Section>
                        {iframeError ? (
                            <Banner tone="warning">
                                Unable to load invoice preview.{' '}
                                <Button url={invoiceUrl} target="_blank" variant="plain">
                                    Open directly in a new tab
                                </Button>
                            </Banner>
                        ) : (
                            <iframe
                                src={proxyUrl!}
                                title="Invoice Preview"
                                style={{ width: '100%', height: '50vh', border: 'none' }}
                                onError={() => setIframeError(true)}
                            />
                        )}
                    </Modal.Section>
                </Modal>
            )}
        </>
    )
}
